import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Info, AlertOctagon, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  fetchActiveBroadcasts,
  isBroadcastDismissed,
  dismissBroadcast,
  type Broadcast,
  type BroadcastSeverity,
} from '../../lib/broadcasts';

const STYLES: Record<
  BroadcastSeverity,
  { wrap: string; icon: string; title: string; Icon: typeof Info }
> = {
  info: {
    wrap: 'bg-info/10 border-info/30',
    icon: 'bg-info/20 text-info',
    title: 'text-info',
    Icon: Info,
  },
  warning: {
    wrap: 'bg-gold-fill/10 border-gold/30',
    icon: 'bg-gold-fill/20 text-gold',
    title: 'text-gold',
    Icon: AlertTriangle,
  },
  critical: {
    wrap: 'bg-danger/10 border-danger/30',
    icon: 'bg-danger/20 text-danger',
    title: 'text-danger',
    Icon: AlertOctagon,
  },
};

/**
 * Global banner for active platform broadcasts. Mounted in DashboardLayout so
 * it appears on every page. Dismiss is per-user (localStorage) and per
 * broadcast id, so a new broadcast always shows even if an old one was closed.
 */
export const BroadcastBanner = () => {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [, force] = useState(0);

  const load = useCallback(async () => {
    setBroadcasts(await fetchActiveBroadcasts());
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel('broadcasts-banner')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'broadcasts' }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [load]);

  const visible = broadcasts.filter((b) => !isBroadcastDismissed(b.id));
  if (visible.length === 0) return null;

  const onDismiss = (id: string) => {
    dismissBroadcast(id);
    force((n) => n + 1);
  };

  return (
    <div className="mb-6 space-y-3">
      {visible.map((b) => {
        const s = STYLES[b.severity] ?? STYLES.info;
        const Icon = s.Icon;
        return (
          <div
            key={b.id}
            role="status"
            className={`w-full border rounded-[var(--fds-radius,12px)] p-4 flex items-start gap-4 ${s.wrap}`}
          >
            <div className={`p-2 rounded-lg shrink-0 ${s.icon}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={`font-bold text-sm uppercase tracking-wider mb-1 ${s.title}`}>
                {b.title}
              </h4>
              <p className="text-fg-2 text-sm leading-relaxed whitespace-pre-line">
                {b.message}
              </p>
            </div>
            <button
              onClick={() => onDismiss(b.id)}
              aria-label="Dismiss"
              className="shrink-0 p-1.5 rounded-lg text-fg-3 hover:text-fg hover:bg-tint/5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
